import { DEFAULT_BASIS, type Basis, type Market, type PriceRunLine, type PriceRuns } from "./api";
import { floorInCurrency, type FloorRow } from "./config";
import { BASIS_LABEL, channelLabel, COPY, humanDate, MARKET_LABEL, money, pct1, signedPct } from "./format";
import { lineTitle, placeName, stageRefusal } from "./price";
import type { ProjectBody, Projection } from "./projection";
import { RRP_COPY, type RrpLookup } from "./rrp";

// C1 v1.26 (c1-commit-review, the owner's ask of 2026-09-30 from Prototype v26): the Review and commit modal. Before the batch
// commits, every saved line shows its current and new price, the gross margin at both (C2's projection, the New price box's own
// read), its floor and its RRP. Every figure is C2's string. C2 judges every line again when the batch commits (owner call B,
// 2026-10-01): the modal states the facts and never predicts a refusal. The commit is today's: the open runs of the markets on
// view, all or nothing (owner ruling A2), applied by hand until c6-channel-write.
// C1 v1.41 (c1-review-remove-narrow-filters, the owner's ask of 2026-10-07): each line carries Remove from the batch, the Detail
// panel's own action, so a line can leave the batch from the review without opening its row.

/** One market's saved lines, as Commit sends them: the open runs of the markets on view that hold a saved line, in the order
 * `commitTarget` lists their run ids. */
export type ReviewGroup = { market: Market; runId: number; lines: PriceRunLine[] };
export function reviewGroups(runs: PriceRuns | undefined, markets: Market[]): ReviewGroup[] {
  return (runs?.runs ?? [])
    .filter((run) => run.state === "open" && markets.includes(run.market))
    .map((run) => ({ market: run.market, runId: run.id, lines: run.lines.filter((line) => line.state === "saved") }))
    .filter((group) => group.lines.length > 0);
}

/** The place a line prices, as Prototype v26 writes it: the channel, then the marketplace ("Amazon · Amazon.de") or, for a store
 * line, the region ("Shopify · EU"); the store's own name would repeat the channel. */
export function reviewPlace(line: PriceRunLine): string {
  return `${channelLabel(line)} · ${line.marketplace_id === "ALL" ? MARKET_LABEL[line.market] : placeName(line)}`;
}

/** A saved line's projection at its saved price on the basis on view, on today: a commit is today's whatever date is picked
 * (c4-as-of-grid ruling Q3). The body is the one the New price box sends for the same row on today (an Amazon line names its
 * marketplace, a store line names none), so a projection the drawer read on today is the modal's too. On a picked date the box's
 * key carries the date (C1 v1.24) and the modal's does not: that read is the modal's own. */
export function lineProjectionBody(line: PriceRunLine, basis: Basis): ProjectBody {
  return {
    xero_sku: line.xero_sku, ...(line.marketplace_id === "ALL" ? {} : { marketplace_id: line.marketplace_id }),
    new_price: line.new_price, ...(basis === DEFAULT_BASIS ? {} : { cogs_basis: basis }),
  };
}

/** A cell of the review: its text, and the note under it (null for none). */
export type ReviewCell = { text: string; note: string | null };

/** The gross margin at the committed price, then at the new price, as C2's projection answers them. A line with no committed
 * price has no current margin: the cell says so ("none → 68.9%"), as the Current column does (INV-3). */
export function grossCell(p: Projection | undefined, failed: boolean): ReviewCell {
  if (!p) return { text: failed ? COMMIT_REVIEW_COPY.unread : COMMIT_REVIEW_COPY.reading, note: null };
  return { text: COMMIT_REVIEW_COPY.gross(p.current ? pct1(p.current.margins.gross_pct) : COPY.none, pct1(p.margins.gross_pct)), note: null };
}

/** The line's own-channel floor from the Config read (undefined while Config is unread), and the override saved with the line.
 * Whether the new price is below the floor is C2's to judge at commit; the cell names the floor and the reason. */
export function floorCell(line: PriceRunLine, floor: FloorRow | null | undefined): ReviewCell {
  const note = line.floor_override_reason ? COMMIT_REVIEW_COPY.override(line.floor_override_reason) : null;
  if (floor === undefined) return { text: COMMIT_REVIEW_COPY.floorUnread, note };
  if (floor === null || floor.floor === null) return { text: COPY.floorLine.none, note };
  const value = money(floor.floor, floor.currency);
  return { text: floorInCurrency(floor, line.currency) ? COPY.floorCell(value) : COPY.floorCellOther(value, floor.currency ?? ""), note };
}

/** The new price against the region RRP, as C2's projection compares them (its deviation, the RRP, whether it is below), and the
 * exception saved with the line. A price below the RRP with no exception on a gated row is named, from the overlay's `gated`;
 * on an ungated row the overlay's own sentence says why none is needed. */
export function rrpCell(line: PriceRunLine, p: Projection | undefined, failed: boolean, lookup: RrpLookup): ReviewCell {
  if (!p) return { text: failed ? COMMIT_REVIEW_COPY.unread : COMMIT_REVIEW_COPY.reading, note: null };
  const vs = p.metrics.vs_rrp_pct;
  const row = lookup.kind === "row" ? lookup.row : null;
  const note = line.rrp_exception_reason && line.rrp_exception_until
    ? RRP_COPY.exception(line.rrp_exception_reason, humanDate(line.rrp_exception_until))
    : vs.below && row ? (row.gated ? RRP_COPY.noException : row.is_anchor ? RRP_COPY.anchorNotGated : RRP_COPY.notGated) : null;
  if (vs.rrp === null) return { text: RRP_COPY.noRrp(MARKET_LABEL[line.market]), note };
  if (vs.comparable === false) return { text: RRP_COPY.notComparable(line.currency), note };
  return { text: COMMIT_REVIEW_COPY.vsRrp(signedPct(vs.value), money(vs.rrp, vs.rrp_currency)), note };
}

/** A refused remove (C1 v1.41), in the Detail panel's words: a viewer's lack of edit rights, else C2's message. */
export function removeRefusal(error: unknown, areaName: string): string {
  return stageRefusal(error, areaName, null, "").text;
}

export const COMMIT_REVIEW_COPY = {
  open: (n: number) => `Review and commit ${n} ${n === 1 ? "price" : "prices"}`,
  title: (n: number) => (n === 0 ? COPY.nothingLeft : `Review ${n} ${n === 1 ? "price" : "prices"} before you commit`),
  description: COPY.commitNote,
  market: (market: Market, n: number) => `${MARKET_LABEL[market]} · ${n} ${n === 1 ? "price" : "prices"}`,
  // c1-run-propose (DEC-1366 B): the price the strategy run proposed sits beside old and new.
  columns: ["Product / Channel", "Current", "New", "Proposed", "Gross margin", "Floor", "RRP"],
  /** The seventh column's header, read by a screen reader only: its cells are the Remove buttons. */
  removeColumn: "Remove",
  gross: (current: string, next: string) => `${current} → ${next}`,
  vsRrp: (deviation: string, rrp: string) => `${deviation} vs RRP ${rrp}`,
  override: (reason: string) => `Override: ${reason}`,
  reading: "Reading…",
  unread: "Could not be read",
  floorUnread: "Checked when you commit",
  // C1 v1.41: Remove from the batch on each line, the Detail panel's words (`COPY.remove`, `COPY.removed`), naming the line.
  remove: COPY.remove,
  removing: "Removing…",
  /** The button's accessible name: the words, then the line, so a screen reader hears which line each Remove takes (round 1, A3). */
  removeLabel: (line: PriceRunLine) => `${COPY.remove}: ${lineTitle(line)}`,
  removed: (line: PriceRunLine) => `Removed ${lineTitle(line)} from the batch. The committed price stands.`,
  /** `legend` is the Price Run's own target sentence (the target, not set, or reading Config), so the gross colour is never the
   * only sign of a margin below target. */
  footnote: (basis: Basis, legend: string) =>
    `Gross margin at the committed price, then at the new price: ${BASIS_LABEL[basis]}, single order, today's fees. ${legend}. ` +
    "C2 checks every line against its floor and RRP again when you commit; if one fails, nothing is committed and the line is named here.",
  cancel: "Cancel",
  confirm: COPY.commitButton,
  committing: "Committing…",
};
